import ky, { HTTPError } from 'ky'
import { handleUnauthorized } from '@agion/auth'
import { config } from '../config'
import { unifiedApi } from '../../lib/api-client'
import { keysToCamel, keysToSnake } from '../lib/utils'
import { UnifiedApiError, UnifiedApiError as ApiError, adapterFetch, adapterError } from '../../lib/api-error'
import { adapterRetryUnlessRefused, retryAfterAnswer } from './query-policies'

// One adapter failure for the admin console (ADR-20260925 rule 1), under the
// name this client's callers import.
export { UnifiedApiError as ApiError }

class ApiClient {
  private client = ky.create({
    prefix: `${config.services.api}/v1`,
    // Preserve Ky 1's successful empty-body result for command endpoints.
    parseJson: text => text === '' ? '' : JSON.parse(text),
    timeout: config.performance.apiTimeout,
    credentials: 'include', // Send cookies for session-based auth
    retry: {
      limit: config.performance.retryAttempts,
      methods: ['get'],
      statusCodes: [408, 413, 429, 500, 502, 503, 504],
      // afterResponse throws ApiError, not ky's HTTPError, so statusCodes
      // never applies to an answered call; a 4xx other than 408 and 429 is
      // not asked again.
      shouldRetry: adapterRetryUnlessRefused,
    },
    fetch: adapterFetch,
    hooks: {
      beforeRequest: [
        ({ request }) => {
          // BFF Pattern: No Authorization header needed
          // Authentication is handled by APISIX forward-auth plugin which:
          // 1. Reads the agion_sso_session cookie from the request
          // 2. Calls auth-service /session/resolve to get the JWT
          // 3. Injects the Authorization header into upstream requests
          //
          // The client just needs credentials: 'include' to send the cookie

          // Add common headers
          request.headers.set('Content-Type', 'application/json')
          request.headers.set('Accept', 'application/json')
          request.headers.set('X-Requested-With', 'XMLHttpRequest') // CSRF protection


        }
      ],
      afterResponse: [
        async (state) => {
          const { response } = state
          if (!response.ok) {
            let errorData: unknown
            try {
              errorData = await response.json()
            } catch {
              errorData = response.statusText
            }
            // Soft-401 (must run BEFORE throwing) — beforeError only fires
            // for ky's own HTTPError, not the ApiError thrown below.
            // See @agion/auth/unauthorized.ts for the full rationale.
            if (response.status === 401) {
              void handleUnauthorized()
            }
            const comeBack = retryAfterAnswer(state, errorData)
            if (comeBack) return comeBack
            throw new ApiError(response.status, response.statusText, errorData)
          }
          return undefined
        }
      ],
      beforeError: [
        ({ error }) => {
          // 401 handling lives in afterResponse — see comment there.
          if (error instanceof HTTPError) {
            if (error.response.status === 403) {
              if (window.location.pathname !== '/unauthorized') {
                window.location.href = '/unauthorized'
              }
            }
          }
          return adapterError(error)
        }
      ]
    }
  })

  // Expose unified API instance for resource-oriented operations
  public unified = unifiedApi

  // Generic CRUD operations with automatic case conversion
  // - Request data: camelCase → snake_case (sent to backend)
  // - Response data: snake_case → camelCase (returned to frontend)
  async get<T>(
    endpoint: string,
    searchParams?: Record<string, string | number>,
    options?: { timeout?: number; retry?: number; signal?: AbortSignal },
  ): Promise<T> {
    const response = await this.client.get(endpoint, {
      searchParams,
      ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
      ...(options?.retry !== undefined ? { retry: options.retry } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
    }).json()
    return keysToCamel<T>(response)
  }

  async post<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await this.client.post(endpoint, { json: snakeCaseData }).json()
    return keysToCamel<T>(response)
  }

  async patch<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await this.client.patch(endpoint, { json: snakeCaseData }).json()
    return keysToCamel<T>(response)
  }

  async put<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await this.client.put(endpoint, { json: snakeCaseData }).json()
    return keysToCamel<T>(response)
  }

  async delete<T = void>(endpoint: string): Promise<T> {
    const response = await this.client.delete(endpoint).json()
    return keysToCamel<T>(response)
  }

  // File upload with progress - response converted to camelCase
  async upload<T>(endpoint: string, file: File, _onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post(endpoint, {
      body: formData,
      hooks: {
        beforeRequest: [
          ({ request }) => {
            // Remove Content-Type for file uploads (let browser set it)
            request.headers.delete('Content-Type')
          }
        ]
      }
    }).json()
    return keysToCamel<T>(response)
  }

  // Streaming for large responses - no case conversion needed for binary streams
  async getStream(endpoint: string): Promise<ReadableStream<Uint8Array> | null> {
    const response = await this.client.get(endpoint)
    return response.body
  }
}

export const api = new ApiClient()

// Alias for backward compatibility
export { api as apiClient }

// Export unified API for new resource-oriented usage
export { unifiedApi } from '../../lib/api-client'
export type { UnifiedAPIClient } from '../../lib/api-client'

// Type-safe API response wrapper
export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// Common API types
export interface BaseEntity {
  id: string
  createdAt: string
  updatedAt: string
}
